import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { firebaseApp } from '../../../infra/firebase/firebaseConfig';
import { logEvent } from '../../../infra/firebase/analytics';
import { AnalyticsEvent, AnalyticsParam } from '../../../domain/analytics';
import { useAuth } from '@breath-lab/state';
import { PageMeta } from '../../seo/PageMeta';
import Button from '@ui/components/Button/Button';
import styles from './ContactPage.module.css';

type Category = 'general' | 'feedback' | 'bug_report' | 'feature_request';
type Platform = 'ios' | 'android' | 'web';

interface ContactFormPayload {
  category: Category;
  platform?: Platform;
  email?: string;
  message: string;
}

export default function ContactPage() {
  const { t } = useTranslation('contact');
  const { user } = useAuth();

  const [category, setCategory] = useState<Category | ''>('');
  const [platform, setPlatform] = useState<Platform | ''>('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    logEvent(AnalyticsEvent.screenView, { [AnalyticsParam.screenName]: 'contact' });
  }, []);

  const isSignedIn = !!user;

  const validate = (): Record<string, string> => {
    const errors: Record<string, string> = {};
    if (!category) errors.category = t('validation.categoryRequired');
    if (category === 'bug_report' && !platform) errors.platform = t('validation.platformRequired');
    if (!isSignedIn) {
      if (!email) errors.email = t('validation.emailRequired');
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errors.email = t('validation.emailInvalid');
    }
    if (!message.trim()) errors.message = t('validation.messageRequired');
    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    setSubmitError('');
    setSubmitting(true);
    try {
      const fns = getFunctions(firebaseApp);
      const submitContactForm = httpsCallable<ContactFormPayload, { success: boolean }>(
        fns,
        'submitContactForm'
      );
      const payload: ContactFormPayload = {
        category: category as Category,
        message: message.trim(),
        ...(category === 'bug_report' && platform ? { platform: platform as Platform } : {}),
        ...(!isSignedIn ? { email } : {}),
      };
      await submitContactForm(payload);
      logEvent(AnalyticsEvent.contactFormSubmitted, { [AnalyticsParam.contentType]: category });
      setSubmitted(true);
    } catch {
      setSubmitError(t('error'));
    } finally {
      setSubmitting(false);
    }
  };

  const categories: { value: Category; label: string }[] = [
    { value: 'general', label: t('categories.general') },
    { value: 'feedback', label: t('categories.feedback') },
    { value: 'bug_report', label: t('categories.bugReport') },
    { value: 'feature_request', label: t('categories.featureRequest') },
  ];

  const platforms: { value: Platform; label: string }[] = [
    { value: 'ios', label: t('platforms.ios') },
    { value: 'android', label: t('platforms.android') },
    { value: 'web', label: t('platforms.web') },
  ];

  const meta = (
    <PageMeta
      title="Contact"
      description="Get in touch with the Breath Lab team — feedback, questions, and anything else on your mind."
      path="/contact"
    />
  );

  if (submitted) {
    return (
      <main className={styles.page}>
        {meta}
        <div className={styles.content}>
          <h1 className={styles.title}>{t('successTitle')}</h1>
          <p className={styles.intro}>{t('successBody')}</p>
        </div>
      </main>
    );
  }

  return (
    <main className={styles.page}>
      {meta}
      <div className={styles.content}>
        <h1 className={styles.title}>{t('title')}</h1>
        <p className={styles.intro}>{t('intro')}</p>

        <form onSubmit={handleSubmit} noValidate className={styles.form}>
          <fieldset className={styles.fieldset}>
            <legend className={styles.fieldLabel}>{t('categoryLabel')}</legend>
            <div className={styles.pillGroup}>
              {categories.map(({ value, label }) => (
                <label
                  key={value}
                  className={`${styles.pill} ${category === value ? styles.pillActive : ''}`}
                >
                  <input
                    type="radio"
                    name="category"
                    value={value}
                    checked={category === value}
                    onChange={() => {
                      setCategory(value);
                      if (value !== 'bug_report') setPlatform('');
                      setFieldErrors(prev => ({ ...prev, category: '', platform: '' }));
                    }}
                    className={styles.srOnly}
                  />
                  {label}
                </label>
              ))}
            </div>
            {fieldErrors.category && <p className={styles.fieldError}>{fieldErrors.category}</p>}
          </fieldset>

          <div
            className={styles.platformSlot}
            style={{ gridTemplateRows: category === 'bug_report' ? '1fr' : '0fr' }}
          >
            <div className={styles.platformInner}>
              <fieldset className={styles.fieldset} style={{ paddingBottom: '0.25rem' }}>
                <legend className={styles.fieldLabel}>{t('platformLabel')}</legend>
                <div className={styles.pillGroup}>
                  {platforms.map(({ value, label }) => (
                    <label
                      key={value}
                      className={`${styles.pill} ${platform === value ? styles.pillActive : ''}`}
                    >
                      <input
                        type="radio"
                        name="platform"
                        value={value}
                        checked={platform === value}
                        onChange={() => {
                          setPlatform(value);
                          setFieldErrors(prev => ({ ...prev, platform: '' }));
                        }}
                        className={styles.srOnly}
                      />
                      {label}
                    </label>
                  ))}
                </div>
                {fieldErrors.platform && <p className={styles.fieldError}>{fieldErrors.platform}</p>}
              </fieldset>
            </div>
          </div>

          {!isSignedIn && (
            <div className={styles.field}>
              <label htmlFor="contact-email" className={styles.fieldLabel}>
                {t('emailLabel')}
              </label>
              <input
                id="contact-email"
                type="email"
                value={email}
                autoComplete="email"
                onChange={e => {
                  setEmail(e.target.value);
                  setFieldErrors(prev => ({ ...prev, email: '' }));
                }}
                onBlur={() => {
                  if (!email) setFieldErrors(prev => ({ ...prev, email: t('validation.emailRequired') }));
                }}
                className={`${styles.input} ${fieldErrors.email ? styles.inputError : ''}`}
              />
              {fieldErrors.email && <p className={styles.fieldError}>{fieldErrors.email}</p>}
            </div>
          )}

          <div className={styles.field}>
            <label htmlFor="contact-message" className={styles.fieldLabel}>
              {t('messageLabel')}
            </label>
            <textarea
              id="contact-message"
              value={message}
              rows={5}
              onChange={e => {
                setMessage(e.target.value);
                setFieldErrors(prev => ({ ...prev, message: '' }));
              }}
              onBlur={() => {
                if (!message.trim()) setFieldErrors(prev => ({ ...prev, message: t('validation.messageRequired') }));
              }}
              className={`${styles.textarea} ${fieldErrors.message ? styles.inputError : ''}`}
            />
            {fieldErrors.message && <p className={styles.fieldError}>{fieldErrors.message}</p>}
          </div>

          {submitError && <p className={styles.submitError}>{submitError}</p>}

          <div>
            <Button type="submit" variant="primary" size="lg" disabled={submitting}>
              {submitting ? t('submitting') : t('submit')}
            </Button>
          </div>
        </form>
      </div>
    </main>
  );
}
